import { useEffect, useRef } from 'react';
import jsQR from 'jsqr';
import { QR_PREFIX } from '../utils/qrHelpers';

const MAX_SCAN_DIMENSION = 720;

/**
 * Continuously scan QR codes from a live video feed (attendance terminal camera).
 */
export function useLiveVideoQrScan({
  videoRef,
  enabled,
  onDetect,
  intervalMs = 180,
}) {
  const onDetectRef = useRef(onDetect);
  onDetectRef.current = onDetect;

  useEffect(() => {
    if (!enabled) return undefined;

    let rafId = 0;
    let lastTick = 0;
    let cancelled = false;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    const tick = (now) => {
      if (cancelled) return;
      rafId = window.requestAnimationFrame(tick);

      if (now - lastTick < intervalMs) return;
      lastTick = now;

      const video = videoRef?.current;
      if (!video || video.readyState < 2 || !video.videoWidth || !video.videoHeight) return;

      const scale = Math.min(
        1,
        MAX_SCAN_DIMENSION / Math.max(video.videoWidth, video.videoHeight)
      );
      canvas.width = Math.floor(video.videoWidth * scale);
      canvas.height = Math.floor(video.videoHeight * scale);
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const result = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'attemptBoth',
      });

      if (!result?.data) return;

      const payload = result.data.replace(/[\r\n]/g, '').trim();
      if (!payload.toUpperCase().startsWith(QR_PREFIX)) return;

      onDetectRef.current(payload);
    };

    rafId = window.requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(rafId);
    };
  }, [enabled, videoRef, intervalMs]);
}
